import { personRef } from "@/content/author";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import bookCover from "@/assets/book-cover-3.webp";
import podcastAsset from "@/assets/yalnizlik-as-podcast.mp3.asset.json";
import { hreflangLinks } from "@/lib/seo";
import { useAuth } from "@/hooks/use-auth";
import { getBookDownloadUrl } from "@/lib/book-download.functions";
import { PageIntro, PageSection, Prose, MoreLink, InfoCard } from "@/components/site/page";

const SITE = "https://akillisurdurulebilirlik.com";
const CANONICAL = `${SITE}/kitaplar/yalnizlik-as`;
const SLUG = "yalnizlik-as";

export const Route = createFileRoute("/kitaplar/yalnizlik-as")({
  head: () => ({
    meta: [
      { title: "Yalnızlık A.Ş. — Arda Öztaşkın" },
      {
        name: "description",
        content:
          "Yalnızlık Nasıl En Kârlı Sektör Haline Geldi? Arda Öztaşkın'ın yeni kitabı. Üye olun, PDF'i ücretsiz indirin.",
      },
      { property: "og:title", content: "Yalnızlık A.Ş. — Arda Öztaşkın" },
      {
        property: "og:description",
        content:
          "Yalnızlık Nasıl En Kârlı Sektör Haline Geldi? Üyelere özel ücretsiz PDF.",
      },
      { property: "og:type", content: "book" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Yalnızlık A.Ş. — Arda Öztaşkın" },
      {
        name: "twitter:description",
        content: "Yalnızlık Nasıl En Kârlı Sektör Haline Geldi? Üyelere özel ücretsiz PDF.",
      },

    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      ...hreflangLinks("/kitaplar/yalnizlik-as"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Book",
          name: "Yalnızlık A.Ş.",
          alternateName: "Yalnızlık Nasıl En Kârlı Sektör Haline Geldi?",
          inLanguage: "tr",
          author: personRef(),
          image: `https://akillisurdurulebilirlik.com${bookCover}`,
          url: CANONICAL,
          bookFormat: "https://schema.org/EBook",
          datePublished: "2026-06",
        }),
      },
    ],
  }),
  component: YalnizlikPage,
});

function DownloadBox() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const fetchUrl = useServerFn(getBookDownloadUrl);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleDownload = async () => {
    setErr("");
    setBusy(true);
    try {
      const res = await fetchUrl({ data: { slug: SLUG } });
      window.location.href = res.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "İndirme başarısız.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <InfoCard><p className="text-sm text-ink/60">Yükleniyor…</p></InfoCard>;
  }

  if (!user) {
    return (
      <InfoCard title="Ücretsiz PDF — Üyelere Özel">
        <p className="font-serif text-base leading-snug text-ink/85">
          Kitabın tam PDF'ini ücretsiz indirebilirsiniz. İndirmek için önce siteye üye olmanız gerekir.
        </p>
        <button
          onClick={() => navigate({ to: "/login", search: { redirect: "/kitaplar/yalnizlik-as" } })}
          className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full border border-ink bg-ink px-5 text-sm text-paper hover:bg-ink/85 transition-colors"
        >
          Üye Ol / Giriş Yap
        </button>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Ücretsiz PDF">
      <p className="font-serif text-base leading-snug text-ink/85">Kitabı hemen indirebilirsiniz.</p>
      <button
        onClick={handleDownload}
        disabled={busy}
        className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full border border-ink bg-ink px-5 text-sm text-paper hover:bg-ink/85 transition-colors disabled:opacity-50"
      >
        {busy ? "Hazırlanıyor…" : "PDF'i indir"}
      </button>
      {err && <p className="mt-2 text-sm text-clay">{err}</p>}
    </InfoCard>
  );
}

function PodcastBox() {
  const [open, setOpen] = useState(false);
  return (
    <InfoCard title="Sesli Özet">
      {open ? (
        <>
          <p className="font-serif text-base leading-snug text-ink/85">Kitap Özeti Podcast</p>
          <audio controls preload="metadata" src={podcastAsset.url} className="mt-3 w-full">
            Tarayıcınız ses oynatmayı desteklemiyor.
          </audio>
        </>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex min-h-11 items-center gap-2 rounded-full border border-ink bg-ink px-5 text-sm text-paper hover:bg-ink/85 transition-colors"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
          Kitap Özeti Podcast
        </button>
      )}
    </InfoCard>
  );
}

function Cover() {
  return (
    <img
      src={bookCover}
      alt="Yalnızlık A.Ş. — Arda Öztaşkın kitap kapağı"
      width={400}
      height={600}
      loading="lazy"
      decoding="async"
      className="aspect-[2/3] w-full max-w-[200px] object-cover shadow-sm lg:max-w-[280px]"
    />
  );
}

function YalnizlikPage() {
  return (
    <div>
      <PageIntro
        crumbs={[{ label: "Kitaplar", to: "/kitaplar" }, { label: "Yalnızlık A.Ş." }]}
        kicker="Yeni kitap · 2026"
        dot="bg-theme-economy"
        title={<>Yalnızlık <em className="italic">A.Ş.</em></>}
        lede="Yalnızlık Nasıl En Kârlı Sektör Haline Geldi?"
        aside={<Cover />}
      >
        <div className="mt-5 lg:hidden"><Cover /></div>
        <div className="mt-6 max-w-sm space-y-4">
          <DownloadBox />
          <PodcastBox />
        </div>
      </PageIntro>

      <PageSection title="Kitap hakkında">
        <Prose>
          <p className="font-serif text-xl italic text-ink">Ya yalnızlık bir duygu değil de bir iş modeli ise?</p>
          <p>
            Bize hep çözümün basit olduğu söylendi: Daha çok dışarı çık, bir kulübe katıl, yeni insanlarla tanış, bir
            uygulama daha indir, kendini sevmeyi öğren. <em>Yalnızlık A.Ş.</em> ise bu tavsiyelerin sessiz bir
            yanıltma olduğunu öne sürüyor. Modern yalnızlık kişisel bir başarısızlık değil. İçinde yaşadığımız
            dünyanın kuruluş biçiminin öngörülebilir, tasarlanmış ve son derece kârlı bir sonucu.
          </p>
          <p>
            Ocak 2018'de Birleşik Krallık bir Yalnızlık Bakanı atadı. 2023'te ise ABD Genel Cerrahı ve Dünya Sağlık
            Örgütü yalnızlığı bir halk sağlığı krizi olarak tanımladı. Yalnızlık artık bireysel bir hüzün olmaktan
            çıkıp toplumsal altyapının bir meselesi hâline geldi. Ve topluluğun bıraktığı boşlukta bugün devasa bir
            endüstri büyüyor.
          </p>
          <p>
            Ekonomi, sosyoloji ve felsefe arasında dolaşan; Polanyi'den Durkheim'a, Putnam'dan Sandel'e,
            Klinenberg'den sosyal bağlara ilişkin güncel araştırmalara uzanan bu kısa ama sarsıcı kitap, rahatsız
            edici bir iddia ortaya koyuyor: Piyasalar aidiyet üretemez. Bu yüzden onun sentetik ikamelerini satarlar.
            Anlamlı olmak yerine görünür olmak geçer. Toplulukların yerini ağlar alır. İhtiyaç duyuluyor olmanın
            işareti üretilir, paketlenir ve abonelik modeliyle bize geri kiralanır.
          </p>
          <p className="font-semibold text-ink">Bu kitapta şunları keşfedeceksiniz:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>Neden dünyanın en hiper bağlantılı şehirleri aynı zamanda tarihin en yüksek yalnızlık oranlarını bildiriyor?</li>
            <li>"Tanınmak" ile "ihtiyaç duyulmak" arasındaki fark nedir ve neden yaşam kalitenizi sessizce belirler?</li>
            <li>Makine müşterilerin ve dikkat ekonomisinin, optimize edilmemiş insanı nasıl giderek gereksiz hâle getirdiğini</li>
            <li>Piyasaların yapısal olarak üretemediği şeylerin — güven, sadakat, aidiyet — neden çıkış yolunun da kendisi olduğunu</li>
          </ul>
          <p>
            Bu bir kişisel gelişim kitabı değil. Sizi rahatlatmaya çalışmayacak. Bunun yerine daha soğuk ama daha işe
            yarar bir araç sunacak: Yalnızlığınızın nasıl başkasının gelir modeline dönüştüğüne dair bir açıklama ve
            bu döngüyü kırmaya başlayabilecek tek soru.
          </p>
          <p className="font-serif text-xl italic text-ink">Hâlâ nerede ve kimin için önem taşıyorsunuz?</p>
        </Prose>
      </PageSection>

      <PageSection title="İçindekiler">
        <div className="grid gap-7 sm:grid-cols-2">
          {[
            { part: "Önsöz", chapters: ["Önsöz"] },
            { part: "Giriş", chapters: ["Ya Yalnızlık Bir Duygu Değilse?"] },
            {
              part: "Bölümler",
              chapters: [
                "Devretme Çağı",
                "Yan Ürünün Ölümü",
                "Homo Economicus'tan Algo Economicus'a",
                "İhtiyaç Duyulmanın Ağırlığı",
                "Anlam Pazarı",
                "Topluluklardan Ağlara",
                "Optimizasyon Mantığı",
                "Boşluğu Doldurma İşi",
                "Aidiyetin Seri Üretimi",
                "Duygusal İkamelerin Ekonomisi",
                "Sentetik Aidiyetin Yükselişi",
                "Sektörün En Büyük Başarısı",
                "Piyasanın Üretemedikleri",
                "Gerekli İnsanların Dönüşü",
              ],
            },
            { part: "Ara Bölüm", chapters: ["Bu Kitaba Karşı İtirazlar"] },
            { part: "Sonsöz", chapters: ["Çıkış, Geri Çekilmek Değildir"] },
            { part: "Ek", chapters: ["Kaynaklar Üzerine Bir Not"] },
          ].map((part) => (
            <div key={part.part} className="space-y-2">
              <h3 className="font-serif text-base text-ink/85">{part.part}</h3>
              <ul className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-ink/75 marker:text-ink/30">
                {part.chapters.map((c) => <li key={c}>{c}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </PageSection>

      <PageSection title="PDF olarak oku">
        <Prose>
          <p>
            Kitabın tam metnine ücretsiz erişebilirsiniz. Tek koşul: <strong>siteye üye olmak</strong>. Üye olduktan
            sonra bu sayfadaki düğmeyle PDF'i hemen indirebilirsiniz.
          </p>
        </Prose>
      </PageSection>

      <PageSection>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-serif text-lg">İngilizce versiyonu için</p>
          <a
            href="https://www.amazon.com/Loneliness-Economy-Liquidation-Belonging-ebook/dp/B0H59HPG62/?_encoding=UTF8&pd_rd_w=qOSlG&content-id=amzn1.sym.3a079c4e-f938-40c9-a0ed-01ef0e9528e9&pf_rd_p=3a079c4e-f938-40c9-a0ed-01ef0e9528e9&pf_rd_r=136-7611256-5080460&pd_rd_wg=ncUP9&pd_rd_r=be08dd99-21b0-4b9e-a00c-845baa3478f3"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-ink/20 px-5 text-sm hover:border-ink transition-colors"
          >
            Amazon'da görüntüle
          </a>
        </div>
      </PageSection>

      <PageSection>
        <p className="text-sm text-ink/70">Yazar</p>
        <p className="mt-1 font-serif text-lg"><Link to="/yazar" className="underline decoration-ink/30 underline-offset-4 hover:decoration-ink">Arda Öztaşkın</Link></p>
        <div className="mt-3"><MoreLink to="/">Ana sayfaya dön</MoreLink></div>
      </PageSection>
    </div>
  );
}
